/**
 * Why the last sign-out happened, carried across the Entra round-trip.
 *
 * `postLogoutRedirectUri` is the app's own origin (App.tsx), so this survives the redirect in the
 * same tab. `sessionStorage` rather than a query param — the reason is not something to bookmark,
 * log or share — and rather than `localStorage`, which would leak the notice into tabs that never
 * idled out.
 */
export const SIGNED_OUT_REASON_KEY = 'cx.signedOutReason'

export type SignedOutReason = 'inactivity'

/** Best-effort: private mode or a full quota must never block the sign-out itself. */
export function markSignedOut(reason: SignedOutReason): void {
  try {
    window.sessionStorage.setItem(SIGNED_OUT_REASON_KEY, reason)
  } catch {
    // no marker, so no notice — the user still gets signed out, which is the important half
  }
}

/**
 * Side-effect free, so it is safe inside a `useState` initializer — StrictMode runs those twice in
 * development and keeps the second result, which a read-and-clear would have turned into null
 * (PR 7037 review). Pair with {@link clearSignedOutReason} in an effect.
 */
export function readSignedOutReason(): SignedOutReason | null {
  try {
    return window.sessionStorage.getItem(SIGNED_OUT_REASON_KEY) === 'inactivity' ? 'inactivity' : null
  } catch {
    return null
  }
}

/** Consumes the marker, so the notice shows once and a reload signs in as usual. Idempotent. */
export function clearSignedOutReason(): void {
  try {
    window.sessionStorage.removeItem(SIGNED_OUT_REASON_KEY)
  } catch {
    // nothing to clear if storage is unavailable
  }
}
